import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  Home, Users, Baby, FileText, IndianRupee, LogOut, 
  ChevronDown, Trophy, Shield, Play, BarChart3, UserPlus, GraduationCap,
  MessageSquare, Video, Calendar, History, Book, TrendingUp, Target, Brain, AlertTriangle, HelpCircle
} from 'lucide-react';

const PrincipalNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const [openDropdown, setOpenDropdown] = useState(null);
  
  const handleLogout = () => {
    localStorage.removeItem('principal_token');
    localStorage.removeItem('principal_user');
    navigate('/principal/login');
  };

  const navGroups = [
    { 
      id: 'home',
      label: 'Home', 
      icon: Home, 
      path: '/principal/dashboard',
      items: null
    },
    { 
      id: 'ai',
      label: 'AI Insights', 
      icon: Brain,
      items: [
        { path: '/principal/ai-dashboard', label: 'AI Dashboard', icon: Brain },
        { path: '/principal/ai-children', label: 'Children Overview', icon: Baby },
        { path: '/principal/ai-observers', label: 'Observer Performance', icon: Users },
        { path: '/principal/ai-escalations', label: 'Escalation Alerts', icon: AlertTriangle },
      ]
    },
    { 
      id: 'team',
      label: 'My Team', 
      icon: Users,
      items: [
        { path: '/principal/observers', label: 'Observers', icon: Users },
        { path: '/principal/recognition', label: 'Recognition', icon: Trophy },
        { path: '/principal/training', label: 'Training', icon: GraduationCap },
        { path: '/principal/meetings', label: 'Meetings', icon: Video },
      ]
    },
    { 
      id: 'students',
      label: 'Students', 
      icon: Baby,
      items: [
        { path: '/principal/child-assignment', label: 'Assignment', icon: UserPlus },
        { path: '/principal/observer-performance', label: 'Performance', icon: BarChart3 },
        { path: '/principal/weekly-questions', label: 'Weekly Questions', icon: MessageSquare },
      ]
    },
    { 
      id: 'reports',
      label: 'Reports', 
      icon: FileText,
      items: [
        { path: '/principal/session-history', label: 'Session History', icon: History },
        { path: '/principal/interactions', label: 'Interactions', icon: MessageSquare },
        { path: '/principal/monthly-reports', label: 'Monthly Reports', icon: TrendingUp },
        { path: '/principal/call-reviews', label: 'Call Reviews', icon: Target },
        { path: '/principal/recordings', label: 'Recordings', icon: Play },
        { path: '/principal/daily-reports', label: "Day's Insights", icon: FileText },
        { path: '/principal/insight-approval', label: 'Approvals', icon: FileText },
      ]
    },
    { 
      id: 'business',
      label: 'Business', 
      icon: IndianRupee,
      items: [
        { path: '/principal/earnings', label: 'Earnings', icon: IndianRupee },
        { path: '/principal/business', label: 'Overview', icon: BarChart3 },
        { path: '/principal/payslips', label: 'Pay Slips', icon: FileText },
      ]
    },
    { 
      id: 'admin',
      label: 'Admin', 
      icon: Shield,
      items: [
        { path: '/principal/policy-book', label: 'Policy Book', icon: Book },
        { path: '/principal/rule-book', label: 'Rule Book (Legacy)', icon: Book },
      ]
    },
    {
      id: 'support',
      label: 'Support',
      icon: HelpCircle,
      path: '/principal/support',
      items: null
    },
  ];

  const isGroupActive = (group) => {
    if (group.path) return currentPath === group.path;
    return group.items?.some(item => currentPath === item.path);
  };

  return (
    <nav className="bg-white border-b shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_sanjaya-ai-1/artifacts/8tjvhfc4_Gemini_Generated_Image_8jnxm88jnxm88jnx.png" 
              alt="Sanjaya Logo" 
              className="w-8 h-8 rounded-lg object-contain"
            />
          </div>

          {/* Navigation - Desktop */}
          <div className="hidden md:flex items-center gap-1">
            {navGroups.map(group => {
              const Icon = group.icon;
              const isActive = isGroupActive(group);
              
              if (!group.items) {
                return (
                  <Button
                    key={group.id}
                    variant={isActive ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => navigate(group.path)}
                    className={isActive ? 'bg-blue-600 text-white' : 'text-gray-600'}
                  >
                    <Icon className="w-4 h-4 mr-1.5" />
                    {group.label}
                  </Button>
                );
              }
              
              return (
                <div key={group.id} className="relative">
                  <Button
                    variant={isActive ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setOpenDropdown(openDropdown === group.id ? null : group.id)}
                    className={isActive ? 'bg-blue-600 text-white' : 'text-gray-600'}
                  >
                    <Icon className="w-4 h-4 mr-1.5" />
                    {group.label}
                    <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${openDropdown === group.id ? 'rotate-180' : ''}`} />
                  </Button>
                  
                  {openDropdown === group.id && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />
                      <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border py-1 min-w-[160px] z-20">
                        {group.items.map(item => {
                          const ItemIcon = item.icon;
                          return (
                            <button
                              key={item.path}
                              onClick={() => { navigate(item.path); setOpenDropdown(null); }}
                              className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 ${
                                currentPath === item.path ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                              }`}
                            >
                              <ItemIcon className="w-4 h-4" />
                              {item.label}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Logout */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-gray-500 hover:text-red-600"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline ml-1">Logout</span>
          </Button>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden overflow-x-auto pb-2 -mx-4 px-4">
          <div className="flex gap-2">
            {navGroups.map(group => {
              const Icon = group.icon;
              const isActive = isGroupActive(group);
              
              if (!group.items) {
                return (
                  <Button
                    key={group.id}
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => navigate(group.path)}
                    className={`whitespace-nowrap ${isActive ? 'bg-blue-600' : ''}`}
                  >
                    <Icon className="w-4 h-4 mr-1" />
                    {group.label}
                  </Button>
                );
              }
              
              return (
                <div key={group.id} className="relative">
                  <Button
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setOpenDropdown(openDropdown === group.id ? null : group.id)}
                    className={`whitespace-nowrap ${isActive ? 'bg-blue-600' : ''}`}
                  >
                    <Icon className="w-4 h-4 mr-1" />
                    {group.label}
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                  
                  {openDropdown === group.id && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />
                      <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border py-1 min-w-[140px] z-20">
                        {group.items.map(item => (
                          <button
                            key={item.path}
                            onClick={() => { navigate(item.path); setOpenDropdown(null); }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                              currentPath === item.path ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                            }`}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default PrincipalNav;
